import clsx from 'clsx';
import { ChevronRight, Trash2 } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Task, TaskPatch, TaskPriority } from '../../api/organizer';
import { isTempTask } from '../../hooks/useOrganizer';
import { DueDateField } from './fields/DueDateField';
import { PriorityField } from './fields/PriorityField';
import { InlineTextEdit } from './fields/InlineTextEdit';
import { Badge } from '../../components/ui/Badge';

export interface TaskCardProps {
  task: Task;
  onPatch: (patch: TaskPatch) => void;
  onOpen: (taskId: string) => void;
  onDelete: (taskId: string) => void;
  /** True while a mutation for this card is in flight. */
  pending?: boolean;
  /** Rendered inside a DragOverlay: no sortable wiring, no hit targets. */
  isOverlay?: boolean;
}

/**
 * One task.
 *
 * A card that only exists optimistically has no server id yet, so it is neither
 * draggable nor editable — dragging a row whose id the server has never seen is
 * the classic way optimistic creates corrupt an ordering.
 */
export function TaskCard({
  task,
  onPatch,
  onOpen,
  onDelete,
  pending,
  isOverlay,
}: TaskCardProps) {
  const isTemp = isTempTask(task);
  const done = task.status === 'done';

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    disabled: isTemp || isOverlay,
  });

  const fromCall = task.source === 'fathom';

  return (
    <div
      ref={isOverlay ? undefined : setNodeRef}
      style={isOverlay ? undefined : { transform: CSS.Transform.toString(transform), transition }}
      {...(isOverlay ? {} : attributes)}
      {...(isOverlay ? {} : listeners)}
      className={clsx(
        'group relative rounded-lg border bg-surface-primary p-3 text-body-sm',
        'border-border-light hover:border-border',
        !isTemp && !isOverlay && 'cursor-grab active:cursor-grabbing',
        isDragging && 'opacity-40',
        isOverlay && 'shadow-lg rotate-1 cursor-grabbing',
        // A card mid-save stays legible but visibly not-yet-settled.
        (pending || isTemp) && 'opacity-60',
      )}
    >
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={done}
          disabled={isTemp}
          aria-label={done ? `Reopen ${task.title}` : `Complete ${task.title}`}
          onPointerDown={(e) => e.stopPropagation()}
          onChange={() => onPatch({ status: done ? 'todo' : 'done' })}
          className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-border accent-black"
        />

        <div className="min-w-0 flex-1 space-y-2">
          <InlineTextEdit
            value={task.title}
            disabled={isTemp}
            muted={done}
            onCommit={(title) => onPatch({ title })}
            className="text-body-sm text-text-primary"
          />

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <DueDateField
              quietWhenEmpty
              value={task.dueDate}
              disabled={isTemp}
              onChange={(dueDate) => onPatch({ dueDate })}
            />
            <PriorityField
              value={task.priority}
              disabled={isTemp}
              onChange={(priority: TaskPriority) => onPatch({ priority })}
            />
            {fromCall && (
              <span title={task.sourceMeta?.meetingTitle ?? 'From a call'}>
                <Badge>From call</Badge>
              </span>
            )}
          </div>
        </div>

        {/* Hover actions float over the card's top-right corner instead of
            reserving a column, so the title wraps at the full card width. */}
        {!isOverlay && !isTemp && (
          <div className="absolute right-2 top-2 flex items-center gap-0.5 rounded bg-surface-primary opacity-0 shadow-subtle transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            <button
              type="button"
              aria-label={`Open ${task.title}`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => onOpen(task.id)}
              className="rounded p-1 text-text-tertiary hover:bg-surface-secondary hover:text-text-primary"
            >
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
            </button>
            <button
              type="button"
              aria-label={`Delete ${task.title}`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => onDelete(task.id)}
              className="rounded p-1 text-text-tertiary hover:bg-error-light hover:text-error-dark"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
